import type { MsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import type { LegacyModelView, LegacyRough } from '@/msgdeskRuntime/model'
import type { AuxiliaryCollectionMode } from '@/msgdeskRuntime/contracts/deskNavigation'

export type LegacyObjectName =
  | 'MSG_OBJ'
  | 'NEW_MSG_OBJ'
  | 'msgObj'
  | 'msgdsk'
  | 'dsk'
  | 'dsk_mode'
  | 'MSG_UTILS'
  | 'all_els_obj'

export class MsgDeskFacade {
  constructor(private readonly runtime: MsgDeskRuntime) {}

  legacyObject<T = any>(name: LegacyObjectName | string): T | undefined {
    return this.runtime.getLegacyObject<T>(name)
  }

  get legacyMsgObj(): any {
    return this.legacyObject('MSG_OBJ')
  }

  get legacyNewMsgObj(): any {
    return this.legacyObject('NEW_MSG_OBJ')
  }

  get appMsgObj(): any {
    return this.legacyObject('msgObj')
  }

  get msgdsk(): any {
    return this.legacyObject('msgdsk')
  }

  get allEls(): any {
    return this.legacyObject('all_els_obj')
  }

  // Typed view over the extracted model core (Stage 1). Null until the
  // runtime has booted; only available on the TS runtime.
  get model(): LegacyModelView | null {
    return (this.runtime.legacyRuntime as any)?.model ?? null
  }

  get utils(): any {
    return this.legacyObject('MSG_UTILS')
      || this.msgdsk?.obj?.('utils')
      || this.legacyObject<any>('dsk')?.obj?.('utils')
  }

  editorRough(): LegacyRough | undefined {
    return this.msgdsk?.mod?.('new_obj')?.mod?.()?.rough?.()
  }

  editorToAddress(): any {
    return this.msgdsk?.mod?.('new_obj')?.mod?.('page_els')?.mod?.('to_address')
  }

  msgData(elId?: number | string): any {
    try {
      const data = this.msgdsk?.obj?.('data')?.msg_data?.(elId);
      if (data) return data;
    } catch {
      // Legacy data helpers can throw during early React mount before boot data is installed.
    }

    try {
      return this.legacyMsgObj?.data_obj?.msg_data?.(elId);
    } catch {
      return undefined;
    }
  }

  msgInputs(elId = 0): Record<string, unknown> | undefined {
    return this.legacyMsgObj?.data_obj?.msg_inputs?.(elId)
  }

  hasRuntimeSubElement(elId: number): boolean {
    return this.legacyMsgObj?.els_obj ? this.legacyMsgObj.els_obj.is_sub_el(elId) !== false : true
  }

  subsTree(modeOrElId: string | number): any {
    return this.legacyMsgObj?.data_obj?.subs_tree_obj?.(modeOrElId)
  }

  deskMode(): unknown {
    const modeFn = this.msgdsk?.obj?.('modes')?.dsk_mode
      || this.legacyObject<any>('dsk')?.obj?.('modes')?.dsk_mode
      || this.legacyObject<any>('dsk_mode')
    return typeof modeFn === 'function' ? modeFn() : modeFn
  }

  editorHandler(name: string): ((...args: any[]) => unknown) | undefined {
    return this.legacyNewMsgObj?.[name] || this.legacyMsgObj?.[name]
  }

  legacyHandler(name: string): ((...args: any[]) => unknown) | undefined {
    return this.legacyMsgObj?.[name] || this.legacyNewMsgObj?.[name]
  }

  appCommand(): any {
    return this.appMsgObj?.command
  }

  checkedIds(section: unknown, mode: string): unknown[] {
    return this.appMsgObj?.subs?.getCheckedIds?.(section, mode) || []
  }

  subRenderState(elId: number): any {
    return this.appMsgObj?.subs?.getSubRenderState?.(elId)
  }

  loadInitialSubs(): unknown {
    const command = this.appCommand()
    if (typeof command?.legacyEvent === 'function') {
      return command.legacyEvent('mev_msg_toggle_menu_subs_evm', null, 'msg', -1)
    }
    return this.legacyHandler('mev_msg_toggle_menu_subs_evm')?.(null, 'msg', -1)
  }

  importAuxiliaryCollectionSpan(
    mode: AuxiliaryCollectionMode,
    section: 'answers' | 'submsges',
    html: string,
  ): void {
    const isSubmsges = section === 'submsges'
    const data = this.msgdsk?.obj?.('data') || this.legacyMsgObj?.data_obj
    const paging = this.msgdsk?.obj?.('paging') || this.legacyMsgObj?.paging_obj
    data?.place_result_html?.(mode, isSubmsges, html, false)
    paging?.init_paging?.(mode, isSubmsges)
  }

  pagingObj(): any {
    return this.legacyMsgObj?.paging_obj
  }

  removeSub(elId: number): unknown {
    return this.legacyMsgObj?.SUBS?.remove_sub?.(elId)
  }

  setDataPayload(legacyKind: string, rawText: string): void {
    this.allEls?.set_data_payload?.(legacyKind, rawText)
  }

  /**
   * BLOCKED-ROWS (2026-07-18): in-place sub unlock — the facade orchestration
   * of legacy _un_lock's sub branch (m09) minus its legacy pin-form DOM reads
   * (the same landmine class the quick-reply fix removed): identical
   * ajax_obj construction, the same get_locked action, the same
   * reimport_data_el + per-place entered-pin memory (msg_data.show_pin), so
   * follow-up per-sub requests ride the pin exactly like the legacy flow.
   * Resolves false on a refused pin; the caller owns feedback/re-projection.
   */
  unlockSub(elId: number, pin: string, isTimeLocked = false): Promise<boolean> {
    return new Promise(resolve => {
      const data = this.msgdsk?.obj?.('data')
      const utils = this.utils
      const msgData = data?.msg_data?.(elId)
      if (!pin || !data?.ajax_obj || !data?.reimport_data_el || !utils?.ajax_get || !msgData) {
        return resolve(false)
      }
      const ajaxObj = data.ajax_obj({
        parent_el_id: msgData['parent_el_id'] || 0,
        is_main_msg: 0,
        is_time_locked: isTimeLocked ? 1 : 0,
        mode_name: 'subs',
        msg_id: msgData['msg_id'],
        code_key: msgData['code_key'],
        is_msg: msgData['is_msg'] ? 1 : 0,
        is_submsges: msgData['is_msg'] ? 1 : 0,
        pin,
      })
      // legacy sets this AFTER ajax_obj() (which overwrites it globally)
      ajaxObj['last_el_id'] = elId - 1
      utils.ajax_get('get_locked', ajaxObj, (resultHtml: unknown) => {
        if (!resultHtml || resultHtml === 'err_pin' || resultHtml === 'err_err_pin') {
          return resolve(false)
        }
        try {
          data.reimport_data_el(elId, resultHtml)
          const fresh = data.msg_data(elId)
          if (fresh) fresh['show_pin'] = pin
          resolve(true)
        } catch {
          resolve(false)
        }
      })
    })
  }

  quickReplyToggleControl(): any {
    return this.msgdsk?.obj?.('els')?.toggle_qr
  }

  toggleQuickReply(elId: number | string): unknown {
    return this.quickReplyToggleControl()?.(elId)
  }
}

export function createMsgDeskFacade(runtime: MsgDeskRuntime): MsgDeskFacade {
  return new MsgDeskFacade(runtime)
}
